/**
 * Blog Data Hooks
 *
 * React Query hooks for fetching blog data from static JSON files.
 * Follows the same pattern as useBillData.ts.
 */

import { useQuery } from '@tanstack/react-query';
import type { BlogIndex, BlogPost } from '../types/blog';

const BLOG_BASE_URL = '/blog';

/**
 * Fetch the blog index with all post summaries
 */
async function fetchBlogIndex(): Promise<BlogIndex> {
  const response = await fetch(`${BLOG_BASE_URL}/blogIndex.json`);
  if (!response.ok) {
    throw new Error(`Failed to fetch blog index: ${response.status}`);
  }
  return response.json();
}

/**
 * Fetch a single blog post by slug
 */
async function fetchBlogPost(slug: string): Promise<BlogPost> {
  const response = await fetch(`${BLOG_BASE_URL}/${slug}.json`);
  if (!response.ok) {
    if (response.status === 404) {
      throw new Error(`Article not found: ${slug}`);
    }
    throw new Error(`Failed to fetch article: ${response.status}`);
  }
  return response.json();
}

/**
 * Hook to fetch the blog index with all post summaries and active categories.
 * Use this for the blog list page.
 */
export function useBlogIndex() {
  return useQuery({
    queryKey: ['blogIndex'],
    queryFn: fetchBlogIndex,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
}

/**
 * Hook to fetch a single blog post by slug.
 * Use this for the blog post detail page.
 */
export function useBlogPost(slug: string | undefined) {
  return useQuery({
    queryKey: ['blogPost', slug],
    queryFn: () => fetchBlogPost(slug!),
    enabled: !!slug, // Only fetch when slug is provided
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
}

/**
 * Hook to get posts filtered by category.
 * Returns the filtered posts from the blog index.
 */
export function useBlogPostsByCategory(category: string | null) {
  const { data: blogIndex, ...rest } = useBlogIndex();

  const filteredPosts = category ? blogIndex?.posts.filter((post) => post.category === category) : blogIndex?.posts;

  return {
    ...rest,
    data: blogIndex
      ? {
          ...blogIndex,
          posts: filteredPosts || [],
        }
      : undefined,
  };
}
